import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, MessageCircle, Mail, Download, UserX, Eye, Filter, RotateCcw } from "lucide-react";
import CustomerDrawer from "@/components/admin/CustomerDrawer";

// Customer phone → wa.me digits (Italian mobile numbers without prefix get +39).
const waNumber = (phone) => {
  let d = String(phone || "").replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.length === 10 && d.startsWith("3")) d = `39${d}`;
  return d.length >= 8 ? d : null;
};

export default function AdminAbandonedCarts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [source, setSource] = useState("");
  const [onlyWithEmail, setOnlyWithEmail] = useState(false);
  const [onlyWithPhone, setOnlyWithPhone] = useState(false);
  const [hoursMax, setHoursMax] = useState(720);
  const [drawerEmail, setDrawerEmail] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/abandoned-carts", {
        params: {
          hours_min: 24,
          hours_max: hoursMax,
          source: source || undefined,
          only_with_email: onlyWithEmail,
          only_with_phone: onlyWithPhone,
          limit: 300,
        },
      });
      setItems(r.data.items || []);
    } catch {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, onlyWithEmail, onlyWithPhone, hoursMax]);

  const sendRecovery = async (id, email) => {
    if (!window.confirm(`Inviare email di recupero a ${email}?`)) return;
    setSendingId(id);
    try {
      await api.post(`/admin/abandoned-carts/${id}/send-recovery-email`);
      toast.success("Email inviata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally { setSendingId(null); }
  };

  const openWhatsApp = (row) => {
    const name = row.customer_name ? row.customer_name.split(" ")[0] : "";
    const greeting = name ? `Ciao ${name}` : "Ciao";
    const msg = `${greeting}, ho visto che avevi messo nel carrello "${row.product_name || "il nostro prodotto"}" su audrivio.com. Posso aiutarti a completare l'ordine? Se hai domande sulla compatibilità con la tua auto sono qui.`;
    window.open(`https://wa.me/${waNumber(row.phone)}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("xd_admin_token");
      const url = `${API}/api/admin/export/abandoned-carts.csv?hours_min=24&hours_max=${hoursMax}${source ? `&source=${source}` : ""}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audrivio-abandoned-carts-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("CSV scaricato");
    } catch { toast.error("Errore export"); }
  };

  const totalValue = items.reduce((s, i) => s + (i.value || 0), 0);
  const recoverable = items.filter((i) => i.email && !i.unsubscribed && !i.recovery_email_sent_at).length;
  const withPhone = items.filter((i) => waNumber(i.phone)).length;

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Recupero vendite</div>
          <h1 className="font-admin text-2xl font-bold flex items-center gap-2"><AlertTriangle className="w-6 h-6 text-amber-500" /> Carrelli abbandonati</h1>
          <p className="text-xs text-xd-muted mt-1">Carrelli non convertiti entro 24h. L'email automatica parte ogni ora sui carrelli tra 24h e 48h.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="inline-flex items-center gap-2 border border-xd-border bg-white hover:bg-slate-50 text-xs font-semibold px-3 py-2 rounded-lg"><RotateCcw className="w-3.5 h-3.5" /> Ricarica</button>
          <button type="button" onClick={exportCsv} data-testid="abandoned-export-csv" className="inline-flex items-center gap-2 bg-xd-dark hover:bg-xd-dark2 text-white text-sm font-semibold px-4 py-2 rounded-lg"><Download className="w-4 h-4" /> Esporta CSV</button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-xd-border rounded-xl p-4">
          <div className="text-xs text-xd-muted">Carrelli abbandonati</div>
          <div className="font-display text-2xl font-semibold text-amber-600 mt-1" data-testid="tile-total">{items.length}</div>
        </div>
        <div className="bg-white border border-xd-border rounded-xl p-4">
          <div className="text-xs text-xd-muted">Valore potenziale</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="tile-value">€ {totalValue.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-xd-border rounded-xl p-4">
          <div className="text-xs text-xd-muted">Recuperabili via email</div>
          <div className="font-display text-2xl font-semibold text-emerald-600 mt-1" data-testid="tile-recoverable">{recoverable}</div>
          <div className="text-[11px] text-xd-muted mt-1" data-testid="tile-with-phone">Con WhatsApp: <span className="font-semibold text-[#1ebe57]">{withPhone}</span></div>
        </div>
        <a
          href={`${API}/api/admin/preview/abandoned-cart-email?lang=it`}
          target="_blank" rel="noreferrer"
          className="bg-white border border-xd-border hover:border-xd-cyan rounded-xl p-4 transition-colors"
        >
          <div className="text-xs text-xd-muted inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Anteprima mail</div>
          <div className="text-sm font-semibold mt-2 text-xd-cyanHover">Apri anteprima →</div>
        </a>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-xd-border p-3 mb-4 flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-xd-muted shrink-0" />
        <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="ab-filter-source" className="border border-xd-border rounded-md px-3 py-2 text-sm bg-white">
          <option value="">Tutte le sorgenti</option>
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="google">Google</option>
          <option value="direct">Diretto</option>
          <option value="other">Altro</option>
        </select>
        <select value={hoursMax} onChange={(e) => setHoursMax(parseInt(e.target.value, 10))} data-testid="ab-filter-window" className="border border-xd-border rounded-md px-3 py-2 text-sm bg-white">
          <option value={48}>Ultime 48h</option>
          <option value={168}>Ultima settimana</option>
          <option value={720}>Ultimo mese</option>
          <option value={4320}>Ultimi 6 mesi</option>
        </select>
        <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
          <input type="checkbox" checked={onlyWithEmail} onChange={(e) => setOnlyWithEmail(e.target.checked)} data-testid="ab-only-email" className="w-4 h-4 accent-xd-cyan" />
          Solo con email identificata
        </label>
        <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
          <input type="checkbox" checked={onlyWithPhone} onChange={(e) => setOnlyWithPhone(e.target.checked)} data-testid="ab-only-phone" className="w-4 h-4 accent-xd-cyan" />
          Solo con telefono (WhatsApp)
        </label>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-xd-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[920px]">
            <thead className="bg-slate-50 border-b border-xd-border">
              <tr className="text-left text-[10px] uppercase font-mono-tech tracking-widest text-xd-muted">
                <th className="p-3">Quando</th>
                <th className="p-3">Prodotto</th>
                <th className="p-3">Cliente</th>
                <th className="p-3">Sorgente</th>
                <th className="p-3">Valore</th>
                <th className="p-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-center text-xd-muted">Caricamento…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-xd-muted">Nessun carrello abbandonato con questi filtri 🎉</td></tr>}
              {items.map((r) => {
                const canEmail = r.email && !r.unsubscribed;
                return (
                  <tr key={r.id} className="border-b border-xd-border last:border-0 hover:bg-slate-50/60" data-testid={`abandoned-row-${r.id}`}>
                    <td className="p-3 whitespace-nowrap text-xs text-xd-muted">{new Date(r.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-3 max-w-[320px]">
                        {r.product_image && <img src={r.product_image} alt="" className="w-10 h-10 rounded-md object-cover border border-xd-border shrink-0" />}
                        <div className="min-w-0">
                          <div className="font-medium text-xs truncate" title={r.product_name}>{r.product_name || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      {r.email ? (
                        <button type="button" onClick={() => setDrawerEmail(r.email)} data-testid={`abandoned-customer-${r.id}`} className="text-left">
                          <div className="font-medium text-xs text-xd-cyanHover hover:underline">{r.customer_name || "Cliente"}</div>
                          <div className="text-[10px] text-xd-muted truncate max-w-[200px]">{r.email}</div>
                          {r.phone && <div className="text-[10px] text-xd-muted" data-testid={`abandoned-phone-${r.id}`}>{r.phone}</div>}
                          {r.stitched && <div className="text-[10px] text-xd-cyanHover" title="Contatto recuperato collegando lo stesso visitatore (cookie Meta / IP + browser)">contatto collegato</div>}
                          {r.unsubscribed && <div className="text-[10px] text-amber-600 inline-flex items-center gap-1 mt-0.5"><UserX className="w-3 h-3" /> opt-out</div>}
                        </button>
                      ) : r.phone ? (
                        <div className="text-xs"><div className="font-medium">{r.customer_name || "Visitatore"}</div><div className="text-[10px] text-xd-muted">{r.phone}</div></div>
                      ) : (
                        <div className="text-xs text-xd-muted italic">Ospite anonimo</div>
                      )}
                    </td>
                    <td className="p-3 text-xs capitalize">{r.source || "—"}</td>
                    <td className="p-3 font-mono-tech text-xs">{r.value ? `€ ${Number(r.value).toFixed(2)}` : "—"}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => openWhatsApp(r)}
                          disabled={!waNumber(r.phone)}
                          title={waNumber(r.phone) ? `WhatsApp ${r.phone}` : "Nessun numero di telefono del cliente"}
                          data-testid={`abandoned-wa-${r.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebe57] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                        >
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                        <button
                          type="button"
                          disabled={!canEmail || sendingId === r.id || !!r.recovery_email_sent_at}
                          onClick={() => sendRecovery(r.id, r.email)}
                          data-testid={`abandoned-email-${r.id}`}
                          title={r.recovery_email_sent_at ? `Già inviata il ${new Date(r.recovery_email_sent_at).toLocaleString("it-IT")}` : (r.unsubscribed ? "Cliente in opt-out" : (!r.email ? "Nessuna email" : "Invia email di recupero"))}
                          className="inline-flex items-center gap-1.5 bg-xd-dark hover:bg-xd-dark2 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-md"
                        >
                          <Mail className="w-3.5 h-3.5" /> {r.recovery_email_sent_at ? "Inviata" : (sendingId === r.id ? "..." : "Email")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CustomerDrawer open={!!drawerEmail} onOpenChange={(o) => !o && setDrawerEmail(null)} email={drawerEmail} />
    </AdminLayout>
  );
}
